/**
 * Search Service
 * Handles all search-related API calls
 */
import api from './api';

// Search interfaces
export interface SearchQuery {
  query: string;
  category?: string;
  contentType?: 'dataAssets' | 'policies' | 'documentation' | 'users';
  limit?: number;
  skip?: number;
}

export interface SearchSuggestion {
  text: string;
  type: string;
  icon: string;
}

export interface SearchResult {
  _id: string;
  contentType: 'dataAsset' | 'policy' | 'documentation' | 'user';
  score: number;
  [key: string]: any; // Additional fields specific to content type
}

export interface SearchResults {
  query: string;
  totalResults: number;
  searchTime: number;
  results: {
    dataAssets: SearchResult[];
    policies: SearchResult[];
    documentation: SearchResult[];
    users: SearchResult[];
  };
}

export interface RecentSearch {
  _id: string;
  query: string;
  resultsCount: number;
  createdAt: string;
}

export interface PopularSearch {
  query: string;
  searchCount: number;
  avgResults: number;
}

/**
 * Perform global search
 */
export const globalSearch = async (searchQuery: SearchQuery): Promise<SearchResults> => {
  try {
    const response = await api.post('/search', searchQuery);
    return response.data.data;
  } catch (error: any) {
    console.error('Search error:', error);
    throw new Error(error.response?.data?.error || 'Search failed');
  }
};

/**
 * Get search suggestions (autocomplete)
 */
export const getSearchSuggestions = async (
  query: string,
  limit: number = 5
): Promise<SearchSuggestion[]> => {
  try {
    const response = await api.get('/search/suggestions', {
      params: { q: query, limit }
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Suggestions error:', error);
    return []; // Return empty array on error to not break UI
  }
};

/**
 * Get user's recent searches
 */
export const getRecentSearches = async (limit: number = 10): Promise<RecentSearch[]> => {
  try {
    const response = await api.get('/search/recent', {
      params: { limit }
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Recent searches error:', error);
    return [];
  }
};

/**
 * Get popular searches
 */
export const getPopularSearches = async (
  limit: number = 10,
  days: number = 7
): Promise<PopularSearch[]> => {
  try {
    const response = await api.get('/search/popular', {
      params: { limit, days }
    });
    return response.data.data;
  } catch (error: any) {
    console.error('Popular searches error:', error);
    return [];
  }
};

/**
 * Track when user clicks on a search result
 */
export const trackResultClick = async (
  searchHistoryId: string,
  resultId: string,
  resultModel: string
): Promise<void> => {
  try {
    await api.post('/search/track-click', {
      searchHistoryId,
      resultId,
      resultModel
    });
  } catch (error: any) {
    // Don't throw - click tracking is not critical
    console.error('Track click error:', error);
  }
};

export default {
  globalSearch,
  getSearchSuggestions,
  getRecentSearches,
  getPopularSearches,
  trackResultClick
};
